import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Input, VStack, Stack } from '@chakra-ui/react'
import { Field } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { propertySchema, type PropertyFormData } from '../validation'
import type { Residence } from '../types'

interface PropertyFormProps {
  property?: Residence
  onSubmit: (data: PropertyFormData) => void
  onCancel: () => void
  isLoading?: boolean
  showActions?: boolean
  formId?: string
  submitLabel?: string
  cancelLabel?: string
}

export function PropertyForm({
  property,
  onSubmit,
  onCancel,
  isLoading,
  showActions = true,
  formId,
  submitLabel,
  cancelLabel,
}: PropertyFormProps) {
  const { t } = useTranslation()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PropertyFormData>({
    resolver: zodResolver(propertySchema),
    defaultValues: property
      ? {
          name: property.name,
          address: property.address,
          city: property.city,
          postal_code: property.postal_code,
          country: property.country,
          residence_key: property.residence_key,
          legal_representative: property.legal_representative ?? '',
          siret: property.siret ?? '',
          bank_name: property.bank_name ?? '',
          iban: property.iban ?? '',
          bic: property.bic ?? '',
          total_shares: property.total_shares ?? undefined,
        }
      : {
          country: 'France',
        },
  })

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)} id={formId}>
      <VStack gap={4} align="stretch">
        <Field.Root invalid={!!errors.residence_key} required>
          <Field.Label>Clé résidence</Field.Label>
          <Input {...register('residence_key')} placeholder="Ex: RES-MONTPARNASSE" />
          {errors.residence_key && (
            <Field.ErrorText>{errors.residence_key.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root invalid={!!errors.name}>
          <Field.Label>{t('properties.fields.name')}</Field.Label>
          <Input {...register('name')} placeholder={t('properties.fields.name')} />
          {errors.name && <Field.ErrorText>{errors.name.message}</Field.ErrorText>}
        </Field.Root>

        <Field.Root invalid={!!errors.total_shares}>
          <Field.Label>{t('properties.fields.total_shares')} (optionnel)</Field.Label>
          <Input
            {...register('total_shares')}
            type="number"
            placeholder="Ex: 10000"
          />
          {errors.total_shares && (
            <Field.ErrorText>{errors.total_shares.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root invalid={!!errors.address}>
          <Field.Label>{t('properties.fields.address')}</Field.Label>
          <Input {...register('address')} placeholder={t('properties.fields.address')} />
          {errors.address && <Field.ErrorText>{errors.address.message}</Field.ErrorText>}
        </Field.Root>

        <Stack direction={{ base: 'column', md: 'row' }} gap={4}>
          <Field.Root invalid={!!errors.postal_code} flex={1}>
            <Field.Label>{t('properties.fields.postal_code')}</Field.Label>
            <Input
              {...register('postal_code')}
              placeholder={t('properties.fields.postal_code')}
            />
            {errors.postal_code && (
              <Field.ErrorText>{errors.postal_code.message}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.city} flex={2}>
            <Field.Label>{t('properties.fields.city')}</Field.Label>
            <Input {...register('city')} placeholder={t('properties.fields.city')} />
            {errors.city && <Field.ErrorText>{errors.city.message}</Field.ErrorText>}
          </Field.Root>
        </Stack>

        <Field.Root invalid={!!errors.country} required>
          <Field.Label>Pays</Field.Label>
          <Input {...register('country')} placeholder="France" defaultValue="France" />
          {errors.country && <Field.ErrorText>{errors.country.message}</Field.ErrorText>}
        </Field.Root>

        <Field.Root invalid={!!errors.legal_representative}>
          <Field.Label>Représentant légal (optionnel)</Field.Label>
          <Input {...register('legal_representative')} placeholder="Nom du représentant" />
          {errors.legal_representative && (
            <Field.ErrorText>{errors.legal_representative.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root invalid={!!errors.siret}>
          <Field.Label>SIRET (optionnel)</Field.Label>
          <Input {...register('siret')} placeholder="12345678901234" />
          {errors.siret && <Field.ErrorText>{errors.siret.message}</Field.ErrorText>}
        </Field.Root>

        <Field.Root invalid={!!errors.bank_name}>
          <Field.Label>Banque (optionnel)</Field.Label>
          <Input {...register('bank_name')} placeholder="Nom de la banque" />
          {errors.bank_name && <Field.ErrorText>{errors.bank_name.message}</Field.ErrorText>}
        </Field.Root>

        <Stack direction={{ base: 'column', md: 'row' }} gap={4}>
          <Field.Root invalid={!!errors.iban} flex={2}>
            <Field.Label>IBAN (optionnel)</Field.Label>
            <Input {...register('iban')} placeholder="FR76 1234 5678 9012 3456 7890 123" />
            {errors.iban && <Field.ErrorText>{errors.iban.message}</Field.ErrorText>}
          </Field.Root>

          <Field.Root invalid={!!errors.bic} flex={1}>
            <Field.Label>BIC (optionnel)</Field.Label>
            <Input {...register('bic')} placeholder="BNPAFRPP" />
            {errors.bic && <Field.ErrorText>{errors.bic.message}</Field.ErrorText>}
          </Field.Root>
        </Stack>

        {showActions && (
          <Box display="flex" gap={3} justifyContent="flex-end" mt={4}>
            <Button variant="outline" onClick={onCancel} disabled={isLoading}>
              {cancelLabel || t('common.cancel')}
            </Button>
            <Button type="submit" colorPalette="brand" loading={isLoading}>
              {submitLabel || t('common.save')}
            </Button>
          </Box>
        )}
      </VStack>
    </Box>
  )
}
